import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { DateFields } from "@/components/DateFields";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { PersonAvatar } from "@/components/PersonAvatar";
import { SpouseSelector } from "@/components/SpouseSelector";
import { AudioRecorder } from "@/components/AudioRecorder";
import { PersonPicker } from "@/components/PersonPicker";
import { normalize, displayName } from "@/lib/names";
import { LINK_TYPES } from "@/lib/relations";

const LinkType = ({ value, onChange, testid }) => (
  <select value={value || "biological"} onChange={e => onChange(e.target.value)} className="mt-1 h-8 rounded-md border border-input bg-transparent px-2 text-xs" data-testid={testid}>
    {Object.entries(LINK_TYPES).map(([k, l]) => <option key={k} value={k}>Lien {l}</option>)}
  </select>
);

const originFields = ["village", "ethnic_group", "clan", "region", "country"];
const empty = {
  origin: {}, paternal_origin: {}, maternal_origin: {}, cultural_identity: "",
  first_name: "", last_name: "", nickname: "", gender: "unknown", clan_key: "",
  birth_year: "", death_year: "", birth_place: "", biography: "",
  father_id: "", mother_id: "", spouse_ids: [], photo_path: null, audio_recordings: [], is_alive: true,
};

export const PersonForm = ({ initial, clans, allPersons, onDone, onCancel }) => {
  const { API } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // `empty` is a module-level constant — safe to omit; `setForm` is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setForm({ ...empty, ...(initial || {}) }); }, [initial]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.path);
      toast.success(t("uploadPhoto") + " ✓");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim()) {
      toast.error(`${t("firstName")} ${t("required")}`);
      return;
    }
    if (!initial?.id) {
      const key = normalize(`${form.last_name}${form.first_name}`);
      const dup = everyone.filter(p => normalize(`${p.last_name}${p.first_name}`) === key || (normalize(p.first_name) === normalize(form.first_name) && normalize(p.last_name) === normalize(form.last_name)));
      if (dup.length && !window.confirm(`Attention, doublon possible : ${dup.map(p => `${displayName(p)}${p.birth_year ? ` (${p.birth_year})` : ""}`).join(", ")}.\n\nCréer quand même une nouvelle fiche ?`)) return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        birth_year: form.birth_year === "" || form.birth_year == null ? null : Number(form.birth_year),
        death_year: form.death_year === "" || form.death_year == null ? null : Number(form.death_year),
        birth_month: form.birth_year ? form.birth_month || null : null,
        birth_day: form.birth_year && form.birth_month ? form.birth_day || null : null,
        death_month: form.death_year ? form.death_month || null : null,
        death_day: form.death_year && form.death_month ? form.death_day || null : null,
        generation_manual: form.generation_manual === "" || form.generation_manual == null ? null : Number(form.generation_manual),
        sort_order: form.sort_order === "" || form.sort_order == null ? null : Number(form.sort_order),
        clan_key: form.clan_key || null,
        father_id: form.father_id || null,
        mother_id: form.mother_id || null,
        is_provisional: false,
        alt_names: (typeof form.alt_names === "string" ? form.alt_names.split(",") : (form.alt_names || [])).map(x => x.trim()).filter(Boolean),
      };
      const { data } = initial?.id ? await axios.put(`${API}/persons/${initial.id}`, payload) : await axios.post(`${API}/persons`, payload);
      if (data?.pending_review) toast.success("Envoyé pour validation : la fiche apparaîtra après accord d'un administrateur.", { duration: 7000 });
      else toast.success(initial?.id ? t("updateSuccess") : t("createSuccess"));
      (data?.warnings || []).forEach(w => toast.warning(w, { duration: 8000 }));
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const [extra, setExtra] = useState([]);
  const addExtra = (p) => setExtra(prev => [...prev, p]);
  const everyone = useMemo(() => [...(allPersons || []), ...extra], [allPersons, extra]);
  const personOptions = useMemo(() => everyone.filter(p => p.id !== initial?.id), [everyone, initial?.id]);
  const fatherOptions = useMemo(() => personOptions.filter(p => p.gender !== "female"), [personOptions]);
  const motherOptions = useMemo(() => personOptions.filter(p => p.gender !== "male"), [personOptions]);

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="person-form">
      <div className="flex items-center gap-4">
        <PersonAvatar person={form} size={72} />
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            data-testid="photo-input"
          />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            variant="outline"
            className="border-[#993D22] text-[#993D22]"
            disabled={uploading}
            data-testid="upload-photo-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {t("uploadPhoto")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("firstName")} *</Label>
          <Input value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="first-name-input" />
        </div>
        <div>
          <Label>{t("lastName")}</Label>
          <Input value={form.last_name || ""} onChange={e => set("last_name", e.target.value)} data-testid="last-name-input" />
        </div>
        <div>
          <Label>{t("nickname")}</Label>
          <Input value={form.nickname || ""} onChange={e => set("nickname", e.target.value)} data-testid="nickname-input" />
          <Label className="mt-2 block">Autres appellations traditionnelles (séparées par des virgules)</Label>
          <Input value={Array.isArray(form.alt_names) ? form.alt_names.join(", ") : (form.alt_names || "")} onChange={e => set("alt_names", e.target.value)} placeholder="ex. Koé Akomo, Etuba Zola" data-testid="alt-names-input" />
        </div>
        <div>
          <Label>{t("gender")}</Label>
          <Select value={form.gender} onValueChange={v => set("gender", v)}>
            <SelectTrigger data-testid="gender-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t("male")}</SelectItem>
              <SelectItem value="female">{t("female")}</SelectItem>
              <SelectItem value="unknown">{t("unknown")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("paternalClan")}</Label>
          <Select value={form.clan_key || "__none"} onValueChange={v => set("clan_key", v === "__none" ? "" : v)}>
            <SelectTrigger data-testid="clan-select"><SelectValue placeholder={t("selectClan")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">—</SelectItem>
              {(clans || []).map(c => (
                <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("birthPlace")}</Label>
          <Input value={form.birth_place || ""} onChange={e => set("birth_place", e.target.value)} data-testid="birth-place-input" />
        </div>
        <DateFields label="Date de naissance" kind="birth" form={form} set={set} />
        <DateFields label="Date de décès" kind="death" form={form} set={set} />

        <div>
          <Label>{t("father")}</Label>
          <PersonPicker value={form.father_id} onChange={v => set("father_id", v)} options={fatherOptions}
            gender="male" onCreated={addExtra} testid="father-picker" placeholder="Nom du père (ex. MBARGA Paul)" />
          <LinkType value={form.father_link_type} onChange={v => set("father_link_type", v)} testid="father-link-type" />
        </div>
        <div>
          <Label>{t("mother")}</Label>
          <PersonPicker value={form.mother_id} onChange={v => set("mother_id", v)} options={motherOptions}
            gender="female" onCreated={addExtra} testid="mother-picker" placeholder="Nom de la mère" />
          <LinkType value={form.mother_link_type} onChange={v => set("mother_link_type", v)} testid="mother-link-type" />
        </div>
        <p className="md:col-span-2 text-xs text-[#6E5B4F] -mt-2">Saisissez simplement un nom : s'il n'a pas encore de profil, une fiche provisoire est créée et pourra être complétée plus tard.</p>

        <div className="flex items-center gap-3 md:col-span-2 pt-2">
          <Switch checked={!!form.is_alive} onCheckedChange={v => set("is_alive", v)} data-testid="is-alive-switch" />
          <Label>{t("isAlive")}</Label>
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch checked={!!form.is_private} onCheckedChange={v => set("is_private", v)} data-testid="is-private-switch" />
          <Label>Fiche privée (masquée aux visiteurs non connectés)</Label>
        </div>
        <details className="md:col-span-2 border border-[#E3D5CA] rounded-lg p-3" data-testid="manual-order">
          <summary className="cursor-pointer text-sm font-semibold text-[#3A2312]">Classement manuel (si les informations sont insuffisantes)</summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Génération imposée (1 = plus ancienne)</Label>
              <Input type="number" min={1} value={form.generation_manual ?? ""} onChange={e => set("generation_manual", e.target.value)} placeholder="Automatique" data-testid="generation-manual-input" />
            </div>
            <div>
              <Label>Rang dans la génération (sans date)</Label>
              <Input type="number" min={0} value={form.sort_order ?? ""} onChange={e => set("sort_order", e.target.value)} placeholder="Automatique" data-testid="sort-order-input" />
            </div>
          </div>
        </details>
      </div>

      <fieldset className="border border-[#E3D5CA] rounded-xl p-4 space-y-4" data-testid="origins-form">
        <legend className="px-2 font-serif-heritage text-xl text-[#261911]">{t("originsTitle")}</legend>
        <p className="text-sm text-[#6E5B4F]">{t("lineagePriority")}</p>
        {["paternal_origin", "maternal_origin", "origin"].map((section, index) => (
          <details key={section} open={index === 0} className={index === 0 ? "border-l-4 border-[#993D22] pl-3" : "border-l-2 border-[#E3D5CA] pl-3"}>
            <summary className="cursor-pointer font-semibold text-[#3A2312]">{t(section === "origin" ? "personalOrigin" : section === "paternal_origin" ? "paternalLineage" : "maternalLineage")}</summary>
            {section !== "origin" && <p className="text-xs text-[#6E5B4F] my-2">{t("originParentHint")}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {originFields.map(key => (
                <div key={key}>
                  <Label htmlFor={`${section}-${key}`}>{t(`origin_${key}`)}</Label>
                  <Input id={`${section}-${key}`} maxLength={200} value={form[section]?.[key] || ""}
                    onChange={e => set(section, { ...(form[section] || {}), [key]: e.target.value })}
                    data-testid={`${section}-${key}`} />
                </div>
              ))}
            </div>
          </details>
        ))}
        <div>
          <Label htmlFor="cultural-identity">{t("culturalIdentity")}</Label>
          <Textarea id="cultural-identity" maxLength={1000} value={form.cultural_identity || ""}
            onChange={e => set("cultural_identity", e.target.value)} placeholder={t("culturalIdentityHint")}
            data-testid="cultural-identity-input" />
          <p className="text-xs text-[#6E5B4F] mt-1">{t("identityNoInference")}</p>
        </div>
      </fieldset>

      <div>
        <Label>{t("biography")}</Label>
        <Textarea
          rows={4}
          value={form.biography || ""}
          onChange={e => set("biography", e.target.value)}
          data-testid="biography-input"
          placeholder={t("oralHistory")}
        />
      </div>

      <div>
        <Label className="mb-2 block">{t("spouses")}</Label>
        <SpouseSelector
          value={form.spouse_ids || []}
          onChange={(v) => set("spouse_ids", v)}
          allPersons={everyone}
          excludeId={initial?.id}
          onCreated={addExtra}
          unionDetails={form.union_details || {}}
          onUnionChange={(sid, u) => set("union_details", { ...(form.union_details || {}), [sid]: { ...((form.union_details || {})[sid] || {}), ...u } })}
        />
      </div>

      <div>
        <Label className="mb-2 block">{t("audioRecordings")}</Label>
        <AudioRecorder
          value={form.audio_recordings || []}
          onChange={(v) => set("audio_recordings", v)}
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="cancel-btn">{t("cancel")}</Button>
        <Button type="submit" className="btn-terra" disabled={saving} data-testid="save-person-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {t("save")}
        </Button>
      </div>
    </form>
  );
};

export default PersonForm;
